import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { MODULE_OPTIONS } from '../auth/roles';
import { formatPortalDate } from '../utils/dateFormat';

function formatDateTime(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatPortalDate(date, { includeTime: true });
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, roleLabel, modulePermissions, logout, defaultPath } = useAuth();
  const displayName = user?.name || user?.email || 'User';
  const initial = displayName.slice(0, 1).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Card className="portal-page-header" sx={{ borderRadius: 4, boxShadow: '0 18px 45px rgba(0,31,63,0.16)', mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          p: { xs: 2.5, md: 4 },
          background: 'radial-gradient(circle at 90% 15%, rgba(77,171,247,0.65) 0%, rgba(77,171,247,0) 36%), linear-gradient(135deg, #001f3f 0%, #003d7a 58%, #1976d2 100%)',
          color: 'white',
          position: 'relative',
        }}>
          <Box sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
            opacity: 0.35,
          }} />
          <Box sx={{ position: 'relative', display: 'flex', gap: 2.5, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
            <Avatar sx={{ width: { xs: 96, md: 120 }, height: { xs: 96, md: 120 }, bgcolor: 'rgba(255,255,255,0.14)', color: 'white', border: '5px solid rgba(255,255,255,0.92)', fontSize: { xs: 38, md: 48 }, fontWeight: 900, boxShadow: '0 16px 35px rgba(0,0,0,0.30)' }}>
              {initial}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" sx={{ fontWeight: 950, color: 'white', fontSize: { xs: '2rem', md: '2.65rem' }, lineHeight: 1 }}>
                {displayName}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: { xs: 'center', sm: 'flex-start' }, color: 'rgba(255,255,255,0.88)', flexWrap: 'wrap' }}>
                <EmailIcon fontSize="small" />
                <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                  {user?.email || 'No email saved'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Chip icon={<SecurityIcon />} label={roleLabel} sx={{ fontWeight: 900, bgcolor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.28)', '& .MuiChip-icon': { color: 'white' } }} />
                <Chip icon={<VerifiedUserIcon />} label={user?.active === false ? 'Inactive' : 'Active'} sx={{ fontWeight: 900, bgcolor: user?.active === false ? 'rgba(148,163,184,0.25)' : 'rgba(34,197,94,0.22)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', '& .MuiChip-icon': { color: 'white' } }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              <Button variant="contained" color="error" startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ fontWeight: 900, boxShadow: 3 }}>
                Log Out
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          { label: 'Role', value: roleLabel, icon: <SecurityIcon />, bg: '#e3f2fd', color: '#1565c0' },
          { label: 'Account', value: user?.active === false ? 'Inactive' : 'Active', icon: <VerifiedUserIcon />, bg: '#e8f5e9', color: '#2e7d32' },
          { label: 'Last Login', value: formatDateTime(user?.last_login_at), icon: <CalendarMonthIcon />, bg: '#fff8e1', color: '#f57c00' },
        ].map((item) => (
          <Grid item xs={12} md={4} key={item.label}>
            <Paper sx={{ p: 2, borderRadius: 3, bgcolor: item.bg, border: `1px solid ${item.color}33`, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ bgcolor: item.color, color: 'white', width: 38, height: 38 }}>{item.icon}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: item.color, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 950, color: '#001f3f', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #d8e2ef', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonIcon sx={{ color: '#001f3f' }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f' }}>Account Details</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {[
              ['Name', user?.name || 'Not set'],
              ['Email', user?.email || 'Not set'],
              ['Role', roleLabel],
              ['Last Login', formatDateTime(user?.last_login_at)],
              ['Created', formatDateTime(user?.created_at)],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.1, borderBottom: '1px solid #eef2f7' }}>
                <Typography color="text.secondary" sx={{ fontWeight: 800 }}>{label}</Typography>
                <Typography sx={{ fontWeight: 900, textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #d8e2ef', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#001f3f' }}>Module Access</Typography>
                <Typography variant="body2" color="text.secondary">Sections this account can open.</Typography>
              </Box>
              <Button variant="outlined" onClick={() => navigate(defaultPath)} sx={{ fontWeight: 900 }}>
                Go To Home
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={1.5}>
              {MODULE_OPTIONS.map((module) => {
                const enabled = modulePermissions?.[module.value] === true;
                return (
                  <Grid item xs={12} sm={6} key={module.value}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: enabled ? '#f0f9ff' : '#f8fafc',
                        borderColor: enabled ? '#90caf9' : '#e2e8f0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{module.label}</Typography>
                        <Chip size="small" label={enabled ? 'Allowed' : 'Blocked'} color={enabled ? 'success' : 'default'} sx={{ fontWeight: 900 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{module.description}</Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
